import classNames from 'classnames';
import * as React from 'react';
import { ReactNode } from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    InputField,
    InputFieldMaskedTextProps,
    InputFieldTextAreaProps,
    InputFieldTextProps,
    InputFieldType
} from '../InputField/InputField';

import './DirectFormInput.css';

export interface DirectFormInputState {
    isFocused: boolean;
}

export interface DirectFormInputTextProps extends InputFieldTextProps {
    count?: number | ReactNode;
}

export interface DirectFormInputTextAreaProps extends InputFieldTextAreaProps {
    count?: number | ReactNode;
}

export interface DirectFormInputMaskedTextProps
    extends InputFieldMaskedTextProps {
    count?: number | ReactNode;
}

export type DirectFormInputProps =
    | DirectFormInputTextProps
    | DirectFormInputTextAreaProps
    | DirectFormInputMaskedTextProps;

export class DirectFormInput extends React.Component<
    DirectFormInputProps,
    DirectFormInputState
> {
    constructor(props: DirectFormInputProps) {
        super(props);

        this.state = { isFocused: false };
    }

    componentDidUpdate(
        prevProps: DirectFormInputProps,
        prevSate: DirectFormInputState
    ) {
        const isFocused = this.state.isFocused;

        if (prevSate.isFocused !== isFocused && isFocused) {
            window.addEventListener('wheel', this.hideHint, {
                once: true,
                capture: true
            });
        }
    }

    render() {
        const { count, inputType, cls } = this.props;

        const hasCounter = count !== undefined;

        return (
            <div
                className={classNames(
                    Bem.cls({
                        block: 'direct-form-input',
                        mods: {
                            counter: hasCounter,
                            middle:
                                hasCounter && inputType === InputFieldType.TEXT
                        }
                    }),
                    cls
                )}
            >
                {this.getInput()}

                {count !== undefined && this.getCounter(count)}
            </div>
        );
    }

    private getCounter = (count: number | ReactNode) => {
        return typeof count === 'number'
            ? this.getDefaultCounter(count)
            : count;
    };

    private getDefaultCounter = (count: number) => {
        return (
            <span
                className={Bem.cls('direct-form-input', 'count', {
                    error: count < 0
                })}
            >
                {count}
            </span>
        );
    };

    private getInput() {
        const { isFocused } = this.state;

        switch (this.props.inputType) {
            case InputFieldType.TEXT: {
                const { errorText, cls, ...inputProps } = this.props;

                return (
                    <InputField
                        {...inputProps}
                        focused={isFocused}
                        hintText={this.getHintText()}
                        errorText={this.getErrorText()}
                        onFocus={this.onFocused}
                        onBlur={this.onBlur}
                    />
                );
            }

            case InputFieldType.TEXTAREA: {
                const { errorText, cls, ...inputProps } = this.props;

                return (
                    <InputField
                        {...inputProps}
                        focused={isFocused}
                        hintText={this.getHintText()}
                        errorText={this.getErrorText()}
                        onFocus={this.onFocused}
                        onBlur={this.onBlur}
                    />
                );
            }

            case InputFieldType.MASKED_TEXT: {
                const { errorText, cls, ...inputProps } = this.props;

                return (
                    <InputField
                        {...inputProps}
                        focused={isFocused}
                        hintText={this.getHintText()}
                        errorText={this.getErrorText()}
                        onFocus={this.onFocused}
                        onBlur={this.onBlur}
                    />
                );
            }

            default:
                throw new Error('unknown InputFieldType');
        }
    }

    private getHintText() {
        const { valid, text, hintText } = this.props;

        return valid && !text ? hintText : null;
    }

    private getErrorText() {
        const { valid, errorText } = this.props;

        return !valid ? errorText : null;
    }

    private onFocused = (event: React.FocusEvent<HTMLElement>) => {
        this.setState({ isFocused: true });

        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    };

    private onBlur = (event: React.FocusEvent<HTMLElement>) => {
        this.setState({ isFocused: false });

        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    };

    private hideHint = () => {
        this.setState({ isFocused: false });
    };
}
